import React, { FunctionComponent } from "react";
import HeaderRow from "./HeaderRow";
import {
  isMedicareAdvantageType,
  isPdPlanType,
  useTranslate,
  useFormatMessage,
} from "../../helpers";
import { Cell, Spacer, FocusableHeader } from "../../components";
import { PlanType, PlanTypeLabels, Plan } from "../../@types";
import GoBack from "./GoBack";

const ComparisonSummary: FunctionComponent<{
  count: number;
  planType: PlanType;
}> = ({ count, planType }) => {
  const FM = useFormatMessage();
  const t = useTranslate();
  const planTypeTranslation = t(PlanTypeLabels[planType]);

  return (
    <div className="planComparisonSummary">
      <FocusableHeader
        className="ds-h1 e2e-comparing-plans"
        text={FM(
          { id: "plan_compare_page.comparing_plans" },
          { count, planTypeTranslation }
        )}
      />
      <GoBack />
    </div>
  );
};

const RowHeading: FunctionComponent<{ title: string }> = ({ title }) => {
  const t = useTranslate();

  return <h4 className="ds-h4 ds-u-margin-y--0">{t(title)}</h4>;
};

const rowHeadings = (headings: string[]): JSX.Element[] =>
  headings.map(heading => (
    <Cell key={heading}>
      <RowHeading title={`plan_terms.${heading}`} />
    </Cell>
  ));

const DrugCostsHeaderRow: FunctionComponent<{ plan: Plan }> = () => (
  <>
    <Cell headerRow>
      <HeaderRow text="plan_terms.drug_coverage_and_costs" />
    </Cell>
    <div className="columnSection">
      <Cell>
        <RowHeading title="plan_terms.drugs_covered" />
      </Cell>
      <Cell>
        <RowHeading title="plan_terms.estimated_yearly_total_cost" />
      </Cell>
    </div>
  </>
);

const MAPDHeadings: FunctionComponent<{ plan: Plan }> = ({ plan }) => (
  <>
    <div className="columnSection">
      {rowHeadings(["premium", "deductible", "oopc_maximum"])}
    </div>

    <Cell headerRow filler>
      <Spacer />
    </Cell>
    <div className="columnSection">{rowHeadings(["plan_features"])}</div>

    <Cell headerRow>
      <HeaderRow text="plan_terms.benefits_and_costs" />
    </Cell>
    <div className="columnSection">
      {rowHeadings([
        "doctor_services",
        "tests_labs_imaging",
        "hospital_services",
        "preventive_services",
      ])}
    </div>

    <Cell headerRow>
      <HeaderRow text="plan_terms.extra_benefits" />
    </Cell>
    <div className="columnSection">
      {rowHeadings([
        "hearing.aids",
        "preventive_dental",
        "comprehensive_dental",
        "vision.eyeglasses",
        "wellness_programs",
        "transportation",
        "skilled_nursing_facility",
        "medical_equipment.durable_medical_equipment",
        "medical_equipment.diabetes_supplies",
      ])}
    </div>

    <DrugCostsHeaderRow plan={plan} />
  </>
);

const PDPHeadings: FunctionComponent<{ plan: Plan }> = ({ plan }) => (
  <>
    {rowHeadings(["premium", "deductible"])}
    <DrugCostsHeaderRow plan={plan} />
  </>
);

const PlanCompareHeadings: FunctionComponent<{
  count: number;
  plan: Plan;
}> = ({ count, plan }) => {
  return (
    <>
      <Cell headerRow>
        <ComparisonSummary
          count={count}
          planType={plan.plan_type as PlanType}
        />
      </Cell>

      <Cell headerRow>
        <HeaderRow text="plan_terms.overview" />
      </Cell>

      {isMedicareAdvantageType(plan.plan_type) && <MAPDHeadings plan={plan} />}
      {isPdPlanType(plan.plan_type) && <PDPHeadings plan={plan} />}

      {/* summary card spacer */}
      <Cell headerRow>
        <Spacer />
      </Cell>
    </>
  );
};

export default PlanCompareHeadings;
